import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';

export function HeroVideoAtmosphere({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'book',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const videoUrl = pickMediaUrl(block, 'heroVideo');
  const posterUrl = pickMediaUrl(block, 'heroPoster');
  const posterSrc = posterUrl ? buildSiteImageProxyUrl(posterUrl, 1600, 78) : undefined;
  const posterSrcSet = posterUrl
    ? buildSiteImageSrcSet(posterUrl, [480, 768, 1200, 1600], 78)
    : undefined;
  const target = isActive ? activeEditTarget : null;
  const imageAlt = heading || eyebrow || 'Atmosphere';

  return (
    <section
      className={['ws-hero-video', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      id="top"
    >
      <div className="ws-hero-video__media" aria-hidden={!videoUrl && !posterSrc}>
        {posterSrc ? (
          <EditableMedia
            fieldKey="heroPoster"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-video__poster"
            src={posterSrc}
            srcSet={posterSrcSet}
            sizes={SITE_IMAGE_SIZES.heroVideo}
            alt={imageAlt}
            width={1600}
            height={900}
            loading="eager"
            decoding="async"
          />
        ) : (
          <div className="ws-hero-video__placeholder" aria-hidden />
        )}
        {videoUrl ? (
          <video
            className="ws-hero-video__video"
            src={videoUrl}
            poster={posterSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : null}
      </div>
      <div id="about" className="ws-hero-video__overlay">
        <EditableContent
          fieldKey="eyebrow"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-video__eyebrow"
        >
          {eyebrow}
        </EditableContent>
        <EditableContent
          as={Heading}
          fieldKey="heading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-video__title"
        >
          {heading}
        </EditableContent>
        <EditableContent
          fieldKey="subheading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-video__subtitle"
        >
          {subheading}
        </EditableContent>
        {ctaPrimary ? (
          <EditableContent
            as="a"
            fieldKey="ctaPrimary"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-video__cta"
            href={ctaPrimaryHref}
          >
            {ctaPrimary}
          </EditableContent>
        ) : null}
      </div>
    </section>
  );
}
